import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchTransfers, createTransfer, disableTransfer, deleteTransfer, fetchItems, fetchFolders, fetchMySpace } from '../services/api';
import type { Transfer, DriveItem, Folder } from '../types';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { Thumbnail } from '../components/TypeGlyph';
import { useAppStore } from '../store/useAppStore';

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function expiryLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Expires today';
  return `${days}d remaining`;
}

// The API's `state` is authoritative; expiry is a second, time-based gate.
function transferStatus(t: Transfer): { label: string; tone: 'success' | 'neutral' | 'danger'; live: boolean } {
  if (t.state === 'revoked') return { label: 'Disabled', tone: 'danger', live: false };
  if (t.state === 'expired' || new Date(t.expires_at).getTime() <= Date.now()) {
    return { label: 'Expired', tone: 'neutral', live: false };
  }
  return { label: 'Active', tone: 'success', live: true };
}

const shareUrl = (token: string) => `${window.location.origin}/t/${token}`;

export default function Transfers() {
  const { toast } = useAppStore();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [expiryDays, setExpiryDays] = useState(7);
  const [pwProtect, setPwProtect] = useState(false);
  const [password, setPassword] = useState('');
  // Selection survives folder navigation, so the item (name/size) is kept, not just the id.
  const [selected, setSelected] = useState<Map<string, DriveItem>>(new Map());
  const [created, setCreated] = useState<Transfer | null>(null);
  const [createdPassword, setCreatedPassword] = useState('');
  const [confirm, setConfirm] = useState<{ kind: 'disable' | 'delete'; transfer: Transfer } | null>(null);

  // Folder navigation inside the picker: [] is the space root.
  const [path, setPath] = useState<Folder[]>([]);
  const currentFolderId = path.length ? path[path.length - 1].id : undefined;

  const { data: transfers = [], isLoading } = useQuery({
    queryKey: qk.transfers(),
    queryFn: fetchTransfers,
  });

  // The picker browses the caller's personal space — `orgId` is a tenant id, not
  // a space id, and would 404 against /folders and /items.
  const { data: mySpace } = useQuery({
    queryKey: ['spaces', 'me'],
    queryFn: fetchMySpace,
    enabled: showCreate,
  });
  const spaceId = mySpace?.id;

  const { data: folders = [] } = useQuery({
    queryKey: qk.folders(spaceId ?? '', currentFolderId),
    queryFn: () => fetchFolders(spaceId!, currentFolderId),
    enabled: showCreate && !!spaceId,
  });

  const { data: driveItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: qk.items(spaceId ?? '', currentFolderId),
    queryFn: () => fetchItems(spaceId!, currentFolderId),
    enabled: showCreate && !!spaceId,
  });

  const itemSize = (f: DriveItem) => Number(f.currentVersion?.size_bytes ?? f.size_bytes ?? 0);
  const selectedList = useMemo(() => [...selected.values()], [selected]);
  const selectedBytes = selectedList.reduce((s, f) => s + itemSize(f), 0);
  const selectedCount = selectedList.length;

  function toggleItem(item: DriveItem) {
    setSelected(prev => {
      const next = new Map(prev);
      next.has(item.id) ? next.delete(item.id) : next.set(item.id, item);
      return next;
    });
  }

  function resetCreateForm() {
    setNewName('');
    setPwProtect(false);
    setPassword('');
    setExpiryDays(7);
    setSelected(new Map());
    setPath([]);
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const expiresAt = new Date(Date.now() + expiryDays * 86400000).toISOString();
      return createTransfer(newName.trim(), [...selected.keys()], expiresAt, pwProtect ? password : undefined);
    },
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: qk.transfers() });
      setCreated(t);
      // Shown once alongside the link — the API only ever stores the hash.
      setCreatedPassword(pwProtect ? password : '');
      setShowCreate(false);
      resetCreateForm();
      toast('Transfer created');
    },
    onError: (e: Error) => toast(e.message || 'Failed to create transfer', 'danger'),
  });

  const disableMutation = useMutation({
    mutationFn: (id: string) => disableTransfer(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.transfers() }); toast('Transfer disabled'); },
    onError: (e: Error) => toast(e.message || 'Failed to disable transfer', 'danger'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTransfer(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.transfers() }); toast('Transfer deleted'); },
    onError: (e: Error) => toast(e.message || 'Failed to delete transfer', 'danger'),
  });

  const canCreate = newName.trim().length > 0 && selectedCount > 0 && (!pwProtect || password.length > 0);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--line)', background: 'var(--card)' }}>
        <div>
          <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Transfers</h2>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginTop: 2 }}>Send copies of files to anyone, with expiry &amp; download tracking.</div>
        </div>
        <Button kind="accent" icon="send" onClick={() => setShowCreate(true)}>Send transfer</Button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0, 1, 2].map(i => <div key={i} className="t-skeleton" style={{ height: 90, borderRadius: 10 }} />)}
          </div>
        ) : transfers.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12, color: 'var(--ink-3)' }}>
            <Icon name="send" size={40} color="var(--ink-4)" />
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink-2)' }}>No transfers yet</div>
            <div style={{ fontSize: 13 }}>Send a bundle of files with a shareable link.</div>
            <Button kind="accent" icon="send" onClick={() => setShowCreate(true)}>Send transfer</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {transfers.map(t => (
              <TransferCard
                key={t.id}
                transfer={t}
                onDisable={() => setConfirm({ kind: 'disable', transfer: t })}
                onDelete={() => setConfirm({ kind: 'delete', transfer: t })}
                onCopy={() => { navigator.clipboard.writeText(shareUrl(t.token)); toast('Link copied'); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Success modal after create */}
      {created && (
        <Modal open onClose={() => { setCreated(null); setCreatedPassword(''); }} title="Transfer created" size="sm">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="t-alert t-alert--success"><Icon name="check" size={14} />Transfer is live and ready to share.</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="t-input t-input--sm"
                readOnly
                value={shareUrl(created.token)}
                style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11 }}
              />
              <Button kind="outline" size="sm" icon="copy" onClick={() => { navigator.clipboard.writeText(shareUrl(created.token)); toast('Link copied'); }}>Copy</Button>
            </div>
            {createdPassword && (
              <div className="t-alert t-alert--warn" style={{ alignItems: 'flex-start', gap: 10 }}>
                <Icon name="lock" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  Recipients need this password — send it separately from the link.
                  <br />
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>{createdPassword}</strong>
                </span>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Disable / delete confirmation */}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.kind === 'delete' ? 'Delete transfer' : 'Disable transfer'}
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button
              kind="danger"
              onClick={() => {
                if (!confirm) return;
                if (confirm.kind === 'delete') deleteMutation.mutate(confirm.transfer.id);
                else disableMutation.mutate(confirm.transfer.id);
                setConfirm(null);
              }}
            >
              {confirm?.kind === 'delete' ? 'Delete' : 'Disable'}
            </Button>
          </>
        }
      >
        {confirm && (confirm.kind === 'delete' ? (
          <span>
            Delete <strong>{confirm.transfer.name}</strong>? The link stops working and the transfer
            disappears from this list, including its view and download history. Your files are not affected.
          </span>
        ) : (
          <span>
            Disable <strong>{confirm.transfer.name}</strong>? The link stops working immediately, but the
            transfer stays here with its history. This can't be undone — you'd need to send a new transfer.
          </span>
        ))}
      </Modal>

      {/* Create modal */}
      <Modal
        open={showCreate}
        onClose={() => { setShowCreate(false); resetCreateForm(); }}
        title="Send a transfer"
        footer={
          <>
            <Button kind="ghost" onClick={() => { setShowCreate(false); resetCreateForm(); }}>Cancel</Button>
            <Button
              kind="accent"
              icon="send"
              loading={createMutation.isPending}
              disabled={!canCreate}
              onClick={() => createMutation.mutate()}
            >
              Create transfer{selectedBytes > 0 ? ` · ${formatBytes(selectedBytes)}` : ''}
            </Button>
          </>
        }
      >
        <div className="t-stack">
          {/* Info alert */}
          <div className="t-alert t-alert--info" style={{ alignItems: 'flex-start', gap: 10 }}>
            <Icon name="send" size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>Recipients download the files as they are now, without touching your live drive. The link expires automatically.</span>
          </div>

          {/* Transfer name */}
          <div className="t-field">
            <label>Transfer name</label>
            <input className="t-input" placeholder="e.g. Brand kit for agency" value={newName} onChange={e => setNewName(e.target.value)} />
          </div>

          {/* File picker with folder navigation */}
          <div className="t-field">
            <label>Files ({selectedCount} selected · {formatBytes(selectedBytes)})</label>

            {/* Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 'var(--fs-xs)', marginBottom: 6 }}>
              <button
                type="button"
                onClick={() => setPath([])}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: path.length ? 'var(--accent)' : 'var(--ink-3)', fontWeight: 600 }}
              >
                My Drive
              </button>
              {path.map((f, i) => (
                <React.Fragment key={f.id}>
                  <span style={{ color: 'var(--ink-4)' }}>/</span>
                  <button
                    type="button"
                    onClick={() => setPath(path.slice(0, i + 1))}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: i === path.length - 1 ? 'var(--ink-3)' : 'var(--accent)', fontWeight: 600 }}
                  >
                    {f.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
              {itemsLoading || !spaceId ? (
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[0, 1, 2].map(i => <div key={i} className="t-skeleton" style={{ height: 28, borderRadius: 6 }} />)}
                </div>
              ) : folders.length === 0 && driveItems.length === 0 ? (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>
                  {path.length ? 'This folder is empty' : 'No files in your drive'}
                </div>
              ) : (
                <>
                  {/* Folders — navigate, never selectable (a transfer bundles files) */}
                  {folders.map(f => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setPath([...path, f])}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', background: 'transparent', border: 'none', borderBottom: '1px solid var(--line)', textAlign: 'left' }}
                    >
                      <span style={{ width: 15, flexShrink: 0 }} />
                      <Icon name="folder" size={18} color="var(--ink-3)" />
                      <span style={{ flex: 1, fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <Icon name="chev-r" size={13} color="var(--ink-4)" />
                    </button>
                  ))}

                  {driveItems.map((f: DriveItem) => {
                    const checked = selected.has(f.id);
                    const size = itemSize(f);
                    return (
                      <label
                        key={f.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', background: checked ? 'var(--accent-soft)' : 'transparent', borderBottom: '1px solid var(--line)' }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleItem(f)}
                          style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }}
                        />
                        <Thumbnail item={f} width={24} height={24} />
                        <span style={{ flex: 1, fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', flexShrink: 0 }}>{size ? formatBytes(size) : '—'}</span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>

            {/* Selection made elsewhere in the tree stays included — show it. */}
            {selectedCount > 0 && (
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                <span>{selectedCount} file{selectedCount === 1 ? '' : 's'} selected across folders</span>
                <button
                  type="button"
                  onClick={() => setSelected(new Map())}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 600 }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Expires + Security row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="t-field" style={{ margin: 0 }}>
              <label>Expires after</label>
              <select className="t-input" value={expiryDays} onChange={e => setExpiryDays(Number(e.target.value))}>
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
            <div className="t-field" style={{ margin: 0 }}>
              <label>Security</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, cursor: 'pointer' }}>
                <div
                  onClick={() => setPwProtect(v => !v)}
                  style={{ position: 'relative', width: 36, height: 20, borderRadius: 999, background: pwProtect ? 'var(--accent)' : 'var(--paper-3)', cursor: 'pointer', flexShrink: 0, transition: 'background var(--t-fast)' }}
                >
                  <div style={{ position: 'absolute', top: 2, left: pwProtect ? 18 : 2, width: 16, height: 16, borderRadius: 999, background: '#fff', transition: 'left var(--t-fast)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>Require password</span>
              </label>
            </div>
          </div>

          {pwProtect && (
            <div className="t-field">
              <label>Password</label>
              <input
                className="t-input"
                type="text"
                autoComplete="off"
                placeholder="Recipients must enter this to open the link"
                value={password}
                onChange={e => setPassword(e.target.value)}
                maxLength={128}
              />
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 4 }}>
                Shown once after creating — SK Drive stores only a hash.
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function TransferCard({ transfer: t, onDisable, onDelete, onCopy }: {
  transfer: Transfer;
  onDisable: () => void;
  onDelete: () => void;
  onCopy: () => void;
}) {
  const status = transferStatus(t);
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>{t.name}</span>
            <Chip tone={status.tone} dot>{status.label}</Chip>
            {t.password_protected && <Chip tone="warn" icon="lock">Password</Chip>}
          </div>
          <div style={{ display: 'flex', gap: 16, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', flexWrap: 'wrap' }}>
            <span>{t.file_count} file{t.file_count === 1 ? '' : 's'} · {formatBytes(Number(t.total_bytes))}</span>
            <span style={{ fontFamily: 'var(--font-mono)' }}>{t.view_count} views · {t.download_count} downloads</span>
            <span>{status.live ? expiryLabel(t.expires_at) : status.label}</span>
          </div>
          {status.live && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input
                className="t-input t-input--sm"
                readOnly
                value={shareUrl(t.token)}
                style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11 }}
              />
              <Button kind="outline" size="sm" icon="copy" onClick={onCopy}>Copy</Button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {status.live && <Button kind="outline" size="sm" onClick={onDisable}>Disable</Button>}
          <Button kind="ghost" size="sm" icon="trash" iconOnly onClick={onDelete} title="Delete transfer" />
        </div>
      </div>
    </div>
  );
}
